import { AxisAlignedBoundingBox, BoundingSphere, Math as CMath, Cartesian2, Cartesian3, Cartographic, HeadingPitchRange, IntersectionTests, Matrix4, Quaternion, Ray, TranslationRotationScale, sampleTerrainMostDetailed, } from "cesium";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { HeightMap } from "../../core/height-map";
import { AssetType } from "../../types";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { TransformationUtil } from "../../utilities/transformation-utils";
import { createDrawing2DImagePrimitive } from "./utils";
import { ViewerCamera } from "../../viewers/viewer-camera";
import { HeightMapUtils, TOTAL_CELL_COUNT_FOR_MEMORY_OPTIMIZATION, } from "../../core/utils";
import { AsyncTaskQueueManager } from "../../utilities/asyncTaskQueueManager";
import { EARTH_RADIUS } from "../../utilities/extra-utils";
export class Drawing2DImage {
    _id;
    _file;
    _fileName;
    _viewer;
    _viewerCamera;
    _site;
    _info;
    _primitive;
    _wgs84CornerPositions;
    _heightMap;
    _cesiumPointSelector;
    get info() {
        return this._info;
    }
    get heightMap() {
        return this._heightMap;
    }
    get fileName() {
        return this._fileName;
    }
    get id() {
        return this._id;
    }
    constructor(args, viewer, site, cesiumPointSelectorFactory) {
        this._viewer = viewer;
        this._viewerCamera = ViewerCamera.getInstance(this._viewer);
        this._site = site;
        this._id = args.assetId;
        this._file = args.file;
        this._fileName = args.file.name;
        this._info = args;
        this._cesiumPointSelector = cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.PriorityBased);
    }
    static async create(args, viewer, site, cesiumPointSelectorFactory) {
        const centerHeight = await EarthBrainUtils.getTerrainHeightFromCartographic(args.centerLongitude, args.centerLatitude, viewer);
        const cornerPositions = EarthBrainUtils.getCornerPositions(args.centerLongitude, args.centerLatitude, args.imageWidth, args.imageHeight, centerHeight, args.rotateDegree);
        const localCornerPositions = await site.convertPointsToLocalSpace(cornerPositions);
        const drawing2DImageArgs = {
            assetId: args.assetId,
            file: args.file,
            transparency: args.transparency,
            isVisible: args.isVisible,
            isDraped: args.isDraped,
            localCornerPositions,
        };
        const instance = new Drawing2DImage(drawing2DImageArgs, viewer, site, cesiumPointSelectorFactory);
        await instance.updateCornerPositions(localCornerPositions);
        instance.render();
        await instance.updateHeightMap();
        return instance;
    }
    async updateHeightMap() {
        if (this.isDraped()) {
            const boundingBox = AxisAlignedBoundingBox.fromPoints(this._info.localCornerPositions);
            const voxelSize = HeightMapUtils.getCellSizeForTotalCellCount(TOTAL_CELL_COUNT_FOR_MEMORY_OPTIMIZATION, boundingBox);
            // const voxelSize= 0.1;
            this._heightMap = HeightMap.createFromBoxForIndexedGeometry(boundingBox, voxelSize);
            const pointsInTrianglesWithoutHeight = [];
            const pointsInTrianglesWithoutHeightIndices = [];
            const centers = this._heightMap.getVoxelCenters(false); // height will be zero
            const heights = Array(centers.length).fill(Number.NaN);
            const rayCastCapablesOnlyTileSets = [];
            this._site.getVisibleRayCastCapables().forEach((value, key) => {
                if (key == AssetType.Point_Cloud || key == AssetType.Design_Data) {
                    rayCastCapablesOnlyTileSets.push(...value);
                }
            });
            for (let i = 0; i < centers.length; i++) {
                // check if point can be projected in the triangle
                const center = centers[i];
                const ray = new Ray(new Cartesian3(center.x, center.y, center.z + 10000), new Cartesian3(0, 0, -1));
                let hit = IntersectionTests.rayTriangle(ray, this._info.localCornerPositions[0], this._info.localCornerPositions[1], this._info.localCornerPositions[2]);
                if (!hit) {
                    hit = IntersectionTests.rayTriangle(ray, this._info.localCornerPositions[1], this._info.localCornerPositions[3], this._info.localCornerPositions[2]);
                }
                if (hit) {
                    let hits = [];
                    if (rayCastCapablesOnlyTileSets.length > 0) {
                        const unfilteredHits = rayCastCapablesOnlyTileSets.map((rc, index) => rc.rayCast(ray));
                        hits = unfilteredHits.filter((hit) => hit !== undefined);
                    }
                    if (hits.length > 0) {
                        const hitWithMaxHeight = hits.reduce((prev, current) => (prev.z > current.z ? prev : current), hits[0]);
                        heights[i] = hitWithMaxHeight.z;
                    }
                    else {
                        pointsInTrianglesWithoutHeight.push(center);
                        pointsInTrianglesWithoutHeightIndices.push(i);
                    }
                }
            }
            const pointsInWGS84 = await TransformationUtil.transformLocalPointsToWGS84InBatches(pointsInTrianglesWithoutHeight, this._site);
            const cartographicPoints = pointsInWGS84.map((p) => Cartographic.fromCartesian(p));
            const cartographicPointsWithHeight = await sampleTerrainMostDetailed(this._viewer.terrainProvider, cartographicPoints);
            const newWGS84Points = cartographicPointsWithHeight.map((p) => Cartographic.toCartesian(p));
            const newLocalPoints = await TransformationUtil.transformWGS84PointsToLocalPointsInBatches(newWGS84Points, this._site);
            for (let i = 0; i < pointsInTrianglesWithoutHeight.length; i++) {
                if (newLocalPoints[i] !== undefined) {
                    heights[pointsInTrianglesWithoutHeightIndices[i]] =
                        newLocalPoints[i].z;
                }
                else {
                    console.log("undefined point found");
                }
            }
            this._heightMap.getObject().heights = new Float32Array(heights);
            this._heightMap.updateHeightOfBoundingBox();
        }
        else {
            const meshGeometry = {
                indices: [0, 1, 2, 1, 3, 2],
                vertices: Cartesian3.packArray([
                    this._info.localCornerPositions[0],
                    this._info.localCornerPositions[1],
                    this._info.localCornerPositions[2],
                    this._info.localCornerPositions[3],
                ]),
            };
            const boundingBox = AxisAlignedBoundingBox.fromPoints(this._info.localCornerPositions);
            const voxelSize = HeightMapUtils.getCellSizeForTotalCellCount(TOTAL_CELL_COUNT_FOR_MEMORY_OPTIMIZATION, boundingBox);
            this._heightMap = HeightMap.FromIndexedGeometry(meshGeometry, voxelSize);
        }
    }
    updateTransparency(alpha = 1.0) {
        this._primitive.appearance.material.uniforms.color.alpha = alpha;
        this._info.transparency = alpha;
    }
    updateVisibility(isVisible) {
        this._primitive.show = isVisible;
    }
    isVisible() {
        return this._primitive.show;
    }
    isDraped() {
        return this._info.isDraped;
    }
    zoomTo() {
        const cameraOffset = new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-90.0), 0.0);
        const boundingSphere = BoundingSphere.fromPoints(this._wgs84CornerPositions);
        this._viewerCamera.rawCamera.flyToBoundingSphere(boundingSphere, {
            offset: cameraOffset,
        });
    }
    dispose() {
        this._viewer.scene.primitives.remove(this._primitive);
    }
    rayCast(ray) {
        return this._heightMap.rayCast(ray);
    }
    tracePoints(points) {
        return this._heightMap.tracePoints(points);
    }
    toJSON() {
        const cornerPositions = this._wgs84CornerPositions;
        const cornerPositionDeg = [];
        for (const corner of cornerPositions) {
            cornerPositionDeg.push(Cartographic.fromCartesian(corner));
        }
        const json = {
            version: 2,
            localCornerPositions: this._info.localCornerPositions.map((corner) => ({
                x: corner.x,
                y: corner.y,
                z: corner.z,
            })),
            cornerPositionsDeg: cornerPositionDeg.map((corner) => ({
                longitude: CMath.toDegrees(corner.longitude),
                latitude: CMath.toDegrees(corner.latitude),
                height: corner.height,
            })),
            isDraped: this._info.isDraped,
            transparency: this._info.transparency,
        };
        return json;
    }
    static async fromJSON(assetId, file, json, viewer, site, cesiumPointSelectorFactory) {
        if (json.version === 0) {
            const args = {
                centerLatitude: json.centerLatitude,
                centerLongitude: json.centerLongitude,
                imageWidth: json.imageWidth,
                imageHeight: json.imageHeight,
                isDraped: typeof json.isDraped === "boolean"
                    ? json.isDraped
                    : json.isDraped === "true",
                isVisible: true,
                transparency: 0.5,
                file: file,
                assetId: assetId,
                rotateDegree: 0,
            };
            return await Drawing2DImage.create(args, viewer, site, cesiumPointSelectorFactory);
        }
        else if (json.version === 1) {
            const cornerPositions = json.cornerPositions.map((corner) => new Cartesian3(corner.x, corner.y, corner.z));
            const localCornerPositions = await site.convertPointsToLocalSpace(cornerPositions);
            const info = {
                localCornerPositions: localCornerPositions,
                assetId: assetId,
                file: file,
                transparency: json.transparency,
                isVisible: true,
                isDraped: json.isDraped,
            };
            const instance = new Drawing2DImage(info, viewer, site, cesiumPointSelectorFactory);
            await instance.updateCornerPositions(localCornerPositions);
            instance.render();
            await instance.updateHeightMap();
            return instance;
        }
        else if (json.version === 2) {
            const info = {
                file: file,
                assetId: assetId,
                localCornerPositions: json.localCornerPositions.map((corner) => new Cartesian3(corner.x, corner.y, corner.z)),
                isDraped: json.isDraped,
                transparency: json.transparency,
                isVisible: true,
            };
            const instance = new Drawing2DImage(info, viewer, site, cesiumPointSelectorFactory);
            const localCornerPositions = json.localCornerPositions.map((corner) => new Cartesian3(corner.x, corner.y, corner.z));
            await instance.updateCornerPositions(localCornerPositions);
            await instance.updateHeightMap();
            instance.render();
            return instance;
        }
    }
    // This is workaround for D3D-376
    // Before the terrain tile is loaded, getSampleMostDetail doesn't work well
    // So we need to wait for the terrain tile to be loaded
    // If CesiumJS resolved this error, we can use `fromJSON` instead.
    static async fromJSONWithCheckTileAvailability(assetId, file, json, viewer, site, cesiumPointSelectorFactory, addReferenceTopologyFn) {
        await AsyncTaskQueueManager.enqueue(async (signal) => {
            let boundingSphere;
            if (json.version === 2) {
                const localCornerPositions = json.localCornerPositions.map((p) => new Cartesian3(p.x, p.y, p.z));
                const wgs84CornerPositions = await site.convertPointsToWGS84(localCornerPositions);
                boundingSphere = BoundingSphere.fromPoints(wgs84CornerPositions);
            }
            else if (json.version === 1) {
                boundingSphere = BoundingSphere.fromPoints(json.cornerPositions.map((p) => new Cartesian3(p.x, p.y, p.z)));
            }
            else if (json.version === 0) {
                // Calculate the half size of the image (in meters)
                const halfWidth = json.imageWidth / 2;
                const halfHeight = json.imageHeight / 2;
                // Calculate the longitude and latitude of the four corners from the center point
                // Calculate the change in longitude and latitude relative to the Earth's circumference
                // Earth's average radius: about 6371km
                const latRadian = CMath.toRadians(json.centerLatitude);
                // Change in degrees per meter in the latitude direction
                const latDegreePerMeter = CMath.toDegrees(1 / EARTH_RADIUS);
                // Change in degrees per meter in the longitude direction (with latitude correction)
                const lngDegreePerMeter = CMath.toDegrees(1 / (EARTH_RADIUS * Math.cos(latRadian)));
                // Calculate the longitude and latitude of the four corners
                // and multiply by 2 to get the full size of the image
                const cornerPoints = [
                    Cartesian3.fromDegrees(json.centerLongitude - halfWidth * lngDegreePerMeter * 2, json.centerLatitude - halfHeight * latDegreePerMeter * 2, 0),
                    Cartesian3.fromDegrees(json.centerLongitude + halfWidth * lngDegreePerMeter * 2, json.centerLatitude - halfHeight * latDegreePerMeter * 2, 0),
                    Cartesian3.fromDegrees(json.centerLongitude - halfWidth * lngDegreePerMeter * 2, json.centerLatitude + halfHeight * latDegreePerMeter * 2, 0),
                    Cartesian3.fromDegrees(json.centerLongitude + halfWidth * lngDegreePerMeter * 2, json.centerLatitude + halfHeight * latDegreePerMeter * 2, 0),
                ];
                boundingSphere = BoundingSphere.fromPoints(cornerPoints);
            }
            // Get the camera's field of view (default is 60 degrees)
            let fov = CMath.toRadians(60);
            if (viewer.camera.frustum.constructor.name === "PerspectiveFrustum") {
                fov = viewer.camera.frustum.fov;
            }
            // Calculate the height so that the entire image fits within the field of view
            const cameraHeight = (boundingSphere.radius / Math.sin(fov / 2)) * 1.2;
            const cameraOffset = new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-90.0), cameraHeight);
            await new Promise((resolve, reject) => {
                const onAbort = () => {
                    console.warn("Task aborted during fly to bounding sphere.");
                    viewer.camera.cancelFlight();
                };
                signal.addEventListener("abort", onAbort, { once: true });
                viewer.camera.flyToBoundingSphere(boundingSphere, {
                    duration: 0.1,
                    offset: cameraOffset,
                    complete: () => resolve(),
                    cancel: () => reject(new Error("Failed to fly to bounding sphere")),
                });
            });
            if (!viewer.scene.globe.tilesLoaded) {
                await new Promise((resolve, reject) => {
                    const onProgress = (remaining) => {
                        if (remaining === 0) {
                            cleanup();
                            resolve();
                        }
                    };
                    const onAbort = () => {
                        console.warn("Task aborted during tile load.");
                        cleanup();
                        reject(new Error("Aborted"));
                    };
                    const cleanup = () => {
                        viewer.scene.globe.tileLoadProgressEvent.removeEventListener(onProgress);
                        signal.removeEventListener("abort", onAbort);
                    };
                    viewer.scene.globe.tileLoadProgressEvent.addEventListener(onProgress);
                    signal.addEventListener("abort", onAbort, { once: true });
                });
            }
            // drawing2dImage cannot be truly canceled
            // so we need to check if the task is aborted
            let drawing2DImage;
            let isAborted = false;
            const onAbort = () => {
                isAborted = true;
                console.warn("Task aborted during drawing2dImage load.");
            };
            signal.addEventListener("abort", onAbort, { once: true });
            drawing2DImage = await Drawing2DImage.fromJSON(assetId, file, json, viewer, site, cesiumPointSelectorFactory);
            if (isAborted) {
                console.warn("loading drawing2dImage aborted");
                drawing2DImage.dispose();
            }
            else {
                addReferenceTopologyFn(drawing2DImage);
            }
        });
    }
    async updateCornerPositions(localCornerPositions) {
        const wgs84CornerPositions = await this._site.convertPointsToWGS84(localCornerPositions);
        if (wgs84CornerPositions.length === 0) {
            throw new Error("Failed to convert points to WGS84");
        }
        this._info.localCornerPositions = localCornerPositions;
        this._wgs84CornerPositions = wgs84CornerPositions;
    }
    render() {
        this._primitive && this._viewer.scene.primitives.remove(this._primitive);
        this._primitive = createDrawing2DImagePrimitive({
            file: this._file,
            assetId: this._id,
            wgs84CornerPositions: this._wgs84CornerPositions,
            isDraped: this._info.isDraped,
            isVisible: this._info.isVisible,
        });
        this._viewer.scene.primitives.add(this._primitive);
    }
    async alignToTwoPoints(args) {
        const { source1, source2, destination1, destination2 } = args;
        const center = new Cartesian3(source1.x, source1.y, 0);
        const sourceVector = Cartesian2.subtract(source2, source1, new Cartesian2());
        const destinationVector = Cartesian2.subtract(destination2, destination1, new Cartesian2());
        // Calculate scale
        const scale = Cartesian2.magnitude(destinationVector) /
            Cartesian2.magnitude(sourceVector);
        // Calculate rotation
        const sourceAngle = Math.atan2(sourceVector.y, sourceVector.x);
        const destAngle = Math.atan2(destinationVector.y, destinationVector.x);
        const rotationAngle = destAngle - sourceAngle;
        // Create transformation matrix
        const rotationQuaternion = Quaternion.fromAxisAngle(new Cartesian3(0, 0, 1), rotationAngle);
        const rotationScale = new TranslationRotationScale(Cartesian3.ZERO, rotationQuaternion, new Cartesian3(scale, scale, 1));
        const rotationScaleMatrix = Matrix4.fromTranslationRotationScale(rotationScale);
        // Transform points
        const localCornerPositionsWrtOrigin = this._info.localCornerPositions.map((p) => Cartesian3.subtract(p, center, new Cartesian3()));
        const newLocalCornerPositionsWrtOrigin = localCornerPositionsWrtOrigin.map((p) => Matrix4.multiplyByPoint(rotationScaleMatrix, p, new Cartesian3()));
        const newLocalCornerPositionsWrtCenter = newLocalCornerPositionsWrtOrigin.map((p) => Cartesian3.add(p, center, new Cartesian3()));
        const translationVector2D = Cartesian2.subtract(destination1, source1, new Cartesian2());
        const translationVector = new Cartesian3(translationVector2D.x, translationVector2D.y, 0);
        const newLocalCornerPositions = newLocalCornerPositionsWrtCenter.map((p) => Cartesian3.add(p, translationVector, new Cartesian3()));
        await this.updateCornerPositions(newLocalCornerPositions);
        this.render();
    }
    getMeshGeometry() {
        return this._heightMap.geometry;
    }
    getElevation() {
        return this._info.localCornerPositions[0].z;
    }
}
//# sourceMappingURL=drawing-2d-image.js.map